import { EventEmitter } from './EventEmitter';
import { BREAKPOINTS, settings } from '@/config';
import type { DeviceType, Orientation, SizesState } from '@/types';

/**
 * Tracks viewport size, device tier, orientation and clamped pixel ratio.
 * Emits a debounced `resize` (coalesced to one per frame) so subsystems resize
 * exactly once even during continuous drag-resizing.
 */
export class Sizes extends EventEmitter<{ resize: SizesState }> {
  width = window.innerWidth;
  height = window.innerHeight;
  pixelRatio = 1;
  aspect = 1;
  device: DeviceType = 'desktop';
  orientation: Orientation = 'landscape';
  touch = false;

  private resizeFrame = 0;

  constructor() {
    super();
    this.touch =
      (typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches) ||
      'ontouchstart' in window;
    this.measure();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
  }

  get state(): SizesState {
    return {
      width: this.width,
      height: this.height,
      pixelRatio: this.pixelRatio,
      aspect: this.aspect,
      device: this.device,
      orientation: this.orientation,
      touch: this.touch,
    };
  }

  private measure(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.pixelRatio = Math.min(window.devicePixelRatio || 1, settings.renderer.maxPixelRatio);
    this.aspect = this.width / Math.max(this.height, 1);
    this.device =
      this.width <= BREAKPOINTS.mobile
        ? 'mobile'
        : this.width <= BREAKPOINTS.tablet
          ? 'tablet'
          : 'desktop';
    this.orientation = this.width >= this.height ? 'landscape' : 'portrait';
  }

  private onResize = (): void => {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.measure();
      this.emit('resize', this.state);
    });
  };

  dispose(): void {
    cancelAnimationFrame(this.resizeFrame);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    this.clearListeners();
  }
}
